<script lang="ts">
  import { onDestroy, onMount } from "svelte"
  import { SyncLoader } from "svelte-loading-spinners"
  import { xhr, convertObjectToUrl, toast, wait } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import _ from "lodash"
  import { ChevronDown } from "lucide-svelte"
  import { appStore } from "$app/store.svelte"
  import Post from "./Post.svelte"
  import NewPost from "./NewPost.svelte"
  import {
    deleteFileFromList,
    uploadFile
  } from "$app/Pages/Plans/Community/utils"
  import {
    showNewPost,
    permission_level,
    channelPosts,
    channel as _channel
  } from "./store"
  import { handleVideoLinkClick } from "$app/utils"

  interface Props {
    channel: ForumChannelType
    channels: ForumChannelType[]
    post: ForumPostType | null
  }

  let { channel, channels, post = $bindable() }: Props = $props()
  let newPost: ForumPostType | null

  appStore.current_channel = channel

  let members: any[] = $state([])

  let params = $state({ order_by: "most_recent" })
  let isLoading = $state(true)

  onMount(async () => {
    loadChannel(params, true)
  })

  let newPostUserId = appStore.current_user.id
  let schedulePost = false
  let currentTime = new Date()
  let schedulePostValue = currentTime.toISOString().slice(0, 16)

  function enableShowNewPost() {
    newPostUserId = appStore.current_user.id
    schedulePost = false
    currentTime = new Date()
    schedulePostValue = currentTime.toISOString().slice(0, 16)
    appStore.showNewPost = true
  }

  let channelPostsEl: HTMLDivElement = $state()

  let page = 1
  let totalPages = 1
  // Get all statements for this section
  async function loadChannel(params: any, reset = true): Promise<void> {
    if (reset) {
      totalPages = 0
      post = null
      page = 1
      appStore.channelPosts = []
      scrollToTop()
    }
    isLoading = true

    if (totalPages && page > totalPages) {
      isLoading = false
      return
    }
    try {
      let queryStr = convertObjectToUrl(params)

      const resp = await xhr.get<{
        posts: ForumPostType[]
        members: any[]
        channel_permission_level: string
        total_pages: string
      }>(
        `/plans/${appStore.plan.id}/community/${appStore.current_channel.id}.json?${queryStr}&page=${page}`
      )

      totalPages = +resp.headers["total-pages"]
      page++
      members = resp.data.members

      if (
        appStore.plan.community_read_only ||
        !appStore.current_user.has_accepted_community_terms
      ) {
        appStore.permission_level = "none"
      } else {
        appStore.permission_level = resp.data.channel_permission_level
      }
      // Only show pinned posts when filtering by Most Recent
      let new_posts: ForumPostType[] = resp.data.posts

      appStore.channelPosts = [...appStore.channelPosts, ...new_posts]

      // window.history.replaceState(params, "Community", `?${convertObjectToUrl({ ...params, channel: channel.slug })}`)
    } catch (e) {
      isLoading = false
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading the posts for this channel", "error")
    } finally {
      isLoading = false
    }
  }

  function sortPosts(posts: ForumPostType[]): ForumPostType[] {
    if (params.order_by === "most_recent") {
      return posts.sort((a, _) => (a.is_pinned ? -1 : 1))
    } else {
      return posts
    }
  }

  let filterSelect = $state("most_recent")
  function updateFilter() {
    params = { order_by: filterSelect }
    loadChannel(params, true)
  }

  function loadMostRecent() {
    filterSelect = "most_recent"
    updateFilter()
  }

  function loadMyPosts() {
    filterSelect = "my_posts"
    updateFilter()
  }

  function loadMostResponses() {
    filterSelect = "most_responses"
    updateFilter()
  }

  let newPostContent = ""
  let savingPost = false
  async function createPost(content: string): Promise<void> {
    if (
      schedulePost &&
      schedulePostValue <= new Date().toISOString().slice(0, 16)
    ) {
      alert("Please select a future date and time for scheduled posts")
      return
    }

    savingPost = true

    let forum_post_data = {
      forum_channel_id: channel.id,
      user_id: newPostUserId,
      content: content
    }

    if (schedulePost) {
      forum_post_data["published_at"] = schedulePostValue
    }

    try {
      const resp = await xhr.post(
        `/plans/${appStore.plan.id}/community_posts.json`,
        {
          forum_post: forum_post_data
        }
      )
      appStore.channelPosts = [resp.data, ...appStore.channelPosts]
      newPost = null
      newPostContent = ""
      files = []
      appStore.showNewPost = false
      toast("Post has been created", "success")
    } catch (e: any) {
      Honeybadger.notify(e)
    } finally {
      savingPost = false
    }
  }

  async function updateNewPost(content: string): Promise<void> {
    savingPost = true

    try {
      const resp = await xhr.patch(
        `/plans/${appStore.plan.id}/community_posts/${newPost?.id}.json`,
        { forum_post: { content } }
      )

      appStore.channelPosts = [resp.data, ...appStore.channelPosts]
      newPost = null
      newPostContent = ""
      files = []
      appStore.showNewPost = false
      toast("Post has been created", "success")
    } catch (e: any) {
      Honeybadger.notify(e)
    } finally {
      savingPost = false
    }
  }

  function savePost(event: any) {
    const content = event.detail

    if (newPost?.id) {
      updateNewPost(content)
      return
    }

    createPost(content)
  }

  const scrollToBottom = async () => {
    window.scroll({
      top: channelPostsEl.scrollHeight,
      behavior: "auto"
    })
  }

  async function deleteFile(file: ActiveStorageType): Promise<void> {
    if (!newPost) return
    file._selected = true
    files = files
    await wait(50)

    if (await deleteFileFromList(file, appStore.plan.id, newPost.id)) {
      file._selected = true
      files = files.filter((f) => f.id !== file.id)
    }

    file._selected = false
    files = files
  }

  const scrollToTop = async () => {
    window.scroll({
      top: 0,
      behavior: "auto"
    })
  }

  let uploadingFile = false
  let files: ActiveStorageType[] = []
  async function addFile(file: File): Promise<void> {
    uploadingFile = true
    try {
      const resp = await uploadFile(file, {
        plan_id: appStore.plan.id,
        channel_id: channel.id,
        id: newPost?.id,
        user_id: newPostUserId
      })

      files = resp.files
      newPost = resp
    } catch (e: any) {
      toast("There was an error uploading your file", "error")
    } finally {
      uploadingFile = false
    }
  }

  let posts: ForumPostType[] = $state([])

  $effect(() => {
    posts = sortPosts(appStore.channelPosts)
  })

  onMount(() => {
    let postsElement = document.addEventListener(
      "click",
      handleVideoLinkClick,
      { passive: false }
    )
  })
  onDestroy(() => {
    document.removeEventListener("click", handleVideoLinkClick)
  })
</script>

<div class="bg-lwp-pale-grey py-6">
  <div
    class="bg-white pt-4 pb-4 px-6 sticky top-0 border-b border-indigo-100 z-20 shadow-lg rounded"
  >
    <div class="flex items-center">
      <h1 class="text-3xl font-bold">{channel.name}</h1>
      {#if members.length > 0}
        <span class="text-sm font-bold ml-4">MEMBERS</span>
        <div class="ml-1 isolate flex -space-x-2 overflow-hidden">
          {#each members.slice(0, 10) as member}
            <img
              class="relative z-30 inline-block h-8 w-8 rounded-full ring-2 ring-white"
              src={member.avatar_thumb_url}
              alt={member.name}
            />
          {/each}
        </div>
        {#if members.length > 10}
          <span class="ml-1 text-base font-bold p-1">+{members.length - 9}</span
          >
        {/if}
      {/if}
    </div>
    {#if ["admin", "author"].includes(appStore.permission_level) || appStore.plan.id === 1}
      <NewPost {channel} {channels} />
    {:else}
      <div class="absolute right-4 top-4">
        <button
          onclick={enableShowNewPost}
          data-name="new-channel-button"
          class="text-sm border border-gray-200 bg-green-600 text-white py-1 px-2 rounded hover:shadow flex items-center"
        >
          <ChevronDown class="size-6 text-gray-100" /> New Post
        </button>
      </div>
    {/if}
  </div>
  <div class="mt-8" bind:this={channelPostsEl}>
    <div class="px-4 sm:px-0 bg-white rounded shadow">
      <div class="sm:hidden">
        <label for="question-tabs" class="sr-only">Select a tab</label>
        <select
          bind:value={filterSelect}
          onchange={updateFilter}
          class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-rose-500"
        >
          <option value="most_recent">Recent</option>

          <option value="my_posts">My Posts</option>

          <option value="most_responses">Most Responses</option>
        </select>
      </div>
      <div class="hidden sm:block">
        <nav
          class="isolate flex divide-x divide-gray-200 rounded-lg shadow"
          aria-label="Tabs"
        >
          <!-- Current: "text-gray-900", Default: "text-gray-500 hover:text-gray-700" -->
          <!-- svelte-ignore a11y_no_static_element_interactions -->
          <button
            onclick={loadMostRecent}
            class="hover:text-gray-700 rounded-l-lg group relative min-w-0 flex-1 overflow-hidden bg-white py-4 px-6 text-center text-sm font-medium hover:bg-gray-50 focus:z-10"
            class:text-gray-900={params.order_by == "most_recent"}
            class:text-gray-500={params.order_by != "most_recent"}
          >
            <span>Recent</span>
            <span
              class="bg-indigo-500 absolute inset-x-0 bottom-0 h-0.5"
              class:bg-indigo-500={params.order_by == "most_recent"}
              class:bg-transparent={params.order_by != "most_recent"}
            ></span>
          </button>

          <!-- svelte-ignore a11y_no_static_element_interactions -->
          <button
            onclick={loadMyPosts}
            class="hover:text-gray-700 group relative min-w-0 flex-1 overflow-hidden bg-white py-4 px-6 text-center text-sm font-medium hover:bg-gray-50 focus:z-10"
            class:text-gray-900={params.order_by == "my_posts"}
            class:text-gray-500={params.order_by != "my_posts"}
          >
            <span>My Posts</span>
            <span
              class="absolute inset-x-0 bottom-0 h-0.5"
              class:bg-indigo-500={params.order_by == "my_posts"}
              class:bg-transparent={params.order_by != "my_posts"}
            ></span>
          </button>

          <!-- svelte-ignore a11y_no_static_element_interactions -->
          <button
            onclick={loadMostResponses}
            class="text-gray-500 hover:text-gray-700 rounded-r-lg group relative min-w-0 flex-1 overflow-hidden bg-white py-4 px-6 text-center text-sm font-medium hover:bg-gray-50 focus:z-10"
            class:text-gray-900={params.order_by == "most_responses"}
            class:text-gray-500={params.order_by != "most_responses"}
          >
            <span>Most Responses</span>
            <span
              class="absolute inset-x-0 bottom-0 h-0.5"
              class:bg-indigo-500={params.order_by == "most_responses"}
              class:bg-transparent={params.order_by != "most_responses"}
            ></span>
          </button>
        </nav>
      </div>
    </div>

    {#key filterSelect}
      {#if post}
        <ul role="list" class="mt-6">
          <Post
            {post}
            showPinned={false}
            showLink={true}
            onopenpost={() => (appStore.showNewPost = false)}
          />
        </ul>
      {:else if isLoading}
        <div class="py-12 flex justify-center w-full">
          <SyncLoader size="240" color="#7572d2" unit="px" duration="1s" />
        </div>
      {:else if appStore.channelPosts.length > 0}
        <ul role="list" class="mt-6 space-y-4">
          {#each posts as post (post.id)}
            <Post
              {post}
              showPinned={filterSelect === "most_recent"}
              showLink={true}
              onopenpost={() => (appStore.showNewPost = false)}
            />
          {/each}
        </ul>
      {:else}
        <div
          class="px-8 py-6 border border-indigo-50 bg-white rounded shadow mt-8"
        >
          {#if params.order_by === "most_recent"}
            <h2 class="text-xl font-bold">No posts here yet...be the first!</h2>
          {:else if params.order_by === "my_posts"}
            <h2 class="text-xl font-bold">
              You have no posts in this channel.
            </h2>
          {:else if params.order_by === "most_responses"}
            <h2 class="text-xl font-bold">No trending posts here yet.</h2>
          {/if}
        </div>
      {/if}
    {/key}
  </div>
</div>
